export const coverGeneratorGuide = {
  id: 'cover-generator',
  title: 'Cover Generator: KDP-Spec Designs in 60 Seconds',
  subtitle: 'Create professional covers that convert browsers into buyers',
  readTime: '5 min',
  difficulty: 'Beginner',
  toolUsed: 'Cover Generator',
  
  sections: [
    {
      title: 'Why Professional Covers Are Critical for Sales',
      content: `
        <p>Your book cover is your #1 sales tool. Studies show that 79% of readers judge a book by its cover, and professional covers can increase sales by up to 300%.</p>
        
        <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 my-6">
          <h4 class="text-red-300 font-semibold mb-2">Common Cover Mistakes That Kill Sales</h4>
          <ul class="text-red-200 space-y-1">
            <li>• Using generic stock photos</li>
            <li>• Wrong dimensions for KDP</li>
            <li>• Text that's unreadable as thumbnails</li>
            <li>• Covers that don't match the genre</li>
            <li>• Poor spine width calculations</li>
          </ul>
        </div>
        
        <p>Our Cover Generator eliminates these problems by creating KDP-compliant covers that follow proven design principles for your specific genre.</p>
        
        <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 my-6">
          <h4 class="text-green-300 font-semibold mb-2">What Our Covers Include</h4>
          <ul class="text-green-200 space-y-1">
            <li>• Exact Amazon KDP dimensions</li>
            <li>• Genre-appropriate design elements</li>
            <li>• Professional typography</li>
            <li>• High-resolution output (300 DPI)</li>
            <li>• Multiple variations for A/B testing</li>
          </ul>
        </div>
      `
    },
    
    {
      title: 'KDP Mode vs. eBook Mode: Choose the Right Format',
      content: `
        <p>Our Cover Generator offers two modes to match your publishing goals:</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4">
            <h4 class="text-purple-300 font-semibold mb-3">KDP Mode (Recommended)</h4>
            <p class="text-gray-300 mb-3">Perfect for Amazon Kindle Direct Publishing:</p>
            <ul class="text-purple-200 text-sm space-y-1">
              <li>• Paperback dimensions (6"×9", 5"×8", etc.)</li>
              <li>• Automatic spine width calculation</li>
              <li>• Print-ready 300 DPI resolution</li>
              <li>• Bleed areas included</li>
              <li>• Back cover design</li>
            </ul>
          </div>
          
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
            <h4 class="text-blue-300 font-semibold mb-3">eBook Mode</h4>
            <p class="text-gray-300 mb-3">Optimized for digital publishing:</p>
            <ul class="text-blue-200 text-sm space-y-1">
              <li>• Standard eBook ratios (1600×2560)</li>
              <li>• Optimized for thumbnail viewing</li>
              <li>• Perfect for Kindle, Apple Books</li>
              <li>• Smaller file sizes</li>
              <li>• Front cover only</li>
            </ul>
          </div>
        </div>
        
        <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 my-6">
          <h4 class="text-yellow-300 font-semibold mb-2">Pro Tip</h4>
          <p class="text-yellow-200">Start with KDP Mode even if you're only publishing eBooks initially. You can always crop the front cover later, but having the full print-ready version gives you more options.</p>
        </div>
      `
    },
    
    {
      title: 'Genre-Specific Design Intelligence',
      content: `
        <p>Our AI understands that different genres require different visual approaches. Here's how we optimize for each:</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-pink-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-pink-400 mr-2 text-lg">♥</span> Romance
            </h4>
            <p class="text-gray-300 text-sm">Elegant typography, warm colors, emotional imagery, couple silhouettes</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-red-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-red-400 mr-2 text-lg">⚡</span> Thriller/Mystery
            </h4>
            <p class="text-gray-300 text-sm">Dark themes, bold contrasts, suspenseful imagery, dramatic lighting</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-blue-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-blue-400 mr-2 text-lg">■</span> Business
            </h4>
            <p class="text-gray-300 text-sm">Clean layouts, professional colors, success imagery, clear hierarchy</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-yellow-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-yellow-400 mr-2 text-lg">★</span> Self-Help
            </h4>
            <p class="text-gray-300 text-sm">Inspiring imagery, bright colors, motivational elements, clear benefits</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-purple-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-purple-400 mr-2 text-lg">◆</span> Fantasy/Sci-Fi
            </h4>
            <p class="text-gray-300 text-sm">Mystical elements, vibrant colors, otherworldly imagery, atmospheric effects</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-green-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-green-400 mr-2 text-lg">●</span> Non-Fiction
            </h4>
            <p class="text-gray-300 text-sm">Authoritative design, readable fonts, credible imagery, academic feel</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-orange-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-orange-400 mr-2 text-lg">◉</span> Cooking/Lifestyle
            </h4>
            <p class="text-gray-300 text-sm">Appetizing colors, clean aesthetics, lifestyle imagery, inviting design</p>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h4 class="text-white font-semibold mb-2 flex items-center">
              <span class="text-indigo-400 mr-2 text-lg">♦</span> Children's Books
            </h4>
            <p class="text-gray-300 text-sm">Playful fonts, bright colors, fun imagery, age-appropriate design</p>
          </div>
        </div>
        
        <p>The AI automatically selects appropriate design elements, color schemes, and typography based on your chosen genre, ensuring your cover fits reader expectations.</p>
      `
    },
    
    {
      title: 'Advanced Customization Options',
      content: `
        <p>While our AI creates great covers automatically, you can guide the process for even better results:</p>
        
        <h4 class="text-white font-semibold mt-6 mb-3">Style Preferences</h4>
        <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
          <p class="text-blue-200 mb-2"><strong>Example inputs:</strong></p>
          <ul class="text-blue-200 text-sm space-y-1">
            <li>• "Minimalist design with bold typography"</li>
            <li>• "Vintage aesthetic with warm earth tones"</li>
            <li>• "Modern corporate look with blue and white"</li>
            <li>• "Fantasy theme with magical elements"</li>
          </ul>
        </div>
        
        <h4 class="text-white font-semibold mt-6 mb-3">Mood Settings</h4>
        <p class="text-gray-300 mb-3">Help the AI understand the emotional tone:</p>
        <ul class="text-gray-300 space-y-1 mb-4">
          <li><strong>Serious:</strong> Professional, authoritative, trustworthy</li>
          <li><strong>Playful:</strong> Fun, energetic, approachable</li>
          <li><strong>Mysterious:</strong> Intriguing, dark, suspenseful</li>
          <li><strong>Inspirational:</strong> Uplifting, motivational, bright</li>
        </ul>
        
        <h4 class="text-white font-semibold mt-6 mb-3">Target Audience</h4>
        <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p class="text-green-200">Specify your readers: "Young professionals aged 25-35" or "Stay-at-home moms" or "Tech entrepreneurs". This helps the AI choose appropriate imagery and design elements.</p>
        </div>
      `
    },
    
    {
      title: 'Multiple Variations for A/B Testing',
      content: `
        <p>Every cover generation creates 3 unique variations, allowing you to test which performs best:</p>
        
        <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 my-6">
          <h4 class="text-purple-300 font-semibold mb-2">A/B Testing Strategy</h4>
          <ol class="text-purple-200 space-y-2">
            <li><strong>1. Upload all variations</strong> to different KDP accounts or use different titles</li>
            <li><strong>2. Track performance</strong> for 2-4 weeks</li>
            <li><strong>3. Identify the winner</strong> based on click-through rates and sales</li>
            <li><strong>4. Use winning design elements</strong> for future books</li>
          </ol>
        </div>
        
        <h4 class="text-white font-semibold mt-6 mb-3">What to Test</h4>
        <ul class="text-gray-300 space-y-2 mb-4">
          <li><strong>Color schemes:</strong> Bright vs. dark themes</li>
          <li><strong>Typography:</strong> Bold vs. elegant fonts</li>
          <li><strong>Imagery:</strong> Abstract vs. literal representations</li>
          <li><strong>Layout:</strong> Centered vs. asymmetrical designs</li>
        </ul>
        
        <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 my-6">
          <h4 class="text-green-300 font-semibold mb-2">Success Story</h4>
          <p class="text-green-200">"I tested 3 cover variations for my business book. The minimalist design outsold the others 2:1. That single cover test increased my monthly revenue from $800 to $1,600." - Mike T.</p>
        </div>
      `
    },
    
    {
      title: 'Technical Specifications & Download',
      content: `
        <p>Our covers meet all technical requirements for professional publishing:</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div class="bg-gray-800/50 rounded-lg p-4">
            <h4 class="text-white font-semibold mb-2">Print Specifications</h4>
            <ul class="text-gray-300 text-sm space-y-1">
              <li>• 300 DPI resolution</li>
              <li>• CMYK color profile</li>
              <li>• 0.125" bleed areas</li>
              <li>• Spine width auto-calculated</li>
              <li>• PDF format ready for upload</li>
            </ul>
          </div>
          
          <div class="bg-gray-800/50 rounded-lg p-4">
            <h4 class="text-white font-semibold mb-2">Digital Specifications</h4>
            <ul class="text-gray-300 text-sm space-y-1">
              <li>• RGB color profile</li>
              <li>• PNG format with transparency</li>
              <li>• Optimized file sizes</li>
              <li>• Multiple resolution options</li>
              <li>• Thumbnail-optimized versions</li>
            </ul>
          </div>
        </div>
        
        <p><strong>Credit Cost:</strong> 15 credits per generation (includes 3 variations). This covers the cost of our advanced AI image generation and professional design algorithms.</p>
        
        <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 my-6">
          <h4 class="text-blue-300 font-semibold mb-2">Speed Advantage</h4>
          <p class="text-blue-200">Traditional cover design takes 3-7 days and costs $200-500. Our AI creates professional covers in 60 seconds for the cost of 15 credits (approximately $3-5 depending on your package).</p>
        </div>
      `
    }
  ],
  
  cta: {
    title: 'Ready to Create Professional Book Covers?',
    description: 'Generate KDP-compliant covers that sell books, not just look pretty.',
    buttonText: 'Start Creating Covers',
    buttonLink: '/register'
  }
};